
import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Form, Row, Col, Button, Badge, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiFilter, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../services/api';

const AuditLogs = () => {
    const [logs, setLogs] = useState([]);
    const [actions, setActions] = useState([]); // List of available actions
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 10,
        total: 0,
        pages: 0
    });
    const [filters, setFilters] = useState({
        action: '',
        detalhes: '',
        start_date: '',
        end_date: ''
    });

    useEffect(() => {
        fetchLogs();
    }, [pagination.page, pagination.per_page]); // Fetch logs when pagination changes

    useEffect(() => {
        fetchActions();
    }, []);

    const fetchActions = async () => {
        try {
            const res = await api.get('/audit-logs/actions');
            setActions(res.data);
        } catch (err) {
            console.error('Erro ao buscar ações', err);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let query = `/audit-logs?page=${pagination.page}&per_page=${pagination.per_page}&`;
            if (filters.action) query += `action=${filters.action}&`;
            if (filters.detalhes) query += `detalhes=${filters.detalhes}&`;
            if (filters.start_date) query += `start_date=${filters.start_date}&`;
            if (filters.end_date) query += `end_date=${filters.end_date}&`;

            const res = await api.get(query);
            setLogs(res.data.logs);
            setPagination(prev => ({
                ...prev,
                total: res.data.total,
                pages: res.data.pages,
                page: res.data.current_page
            }));
        } catch (err) {
            console.error('Erro ao buscar logs', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on search
        fetchLogs();
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePerPageChange = (e) => {
        setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value), page: 1 }));
    };

    return (
        <Container className="mt-4">
            <div className="d-flex align-items-center mb-4">
            <Button 
                variant="link" 
                as={Link} 
                to="/dashboard" 
                className="text-decoration-none p-0 mb-3 text-muted"
            >
                <FiArrowLeft className="me-1" /> Voltar ao Dashboard
            </Button>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold mb-0" style={{ color: '#006064' }}>Logs de Auditoria</h2>
                <Button variant="outline-primary" onClick={fetchLogs} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spin' : ''} /> Atualizar
                </Button>
            </div>

            <Card className="shadow-sm border-0 mb-4" style={{ backgroundColor: '#F5F7FA' }}>
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3 align-items-end">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted">Tipo de Ação</Form.Label>
                                    <Form.Select 
                                        name="action"
                                        value={filters.action}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">Todas as Ações</option>
                                        {actions.map((action, idx) => (
                                            <option key={idx} value={action}>{action}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted">Detalhes</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Buscar nos detalhes..." 
                                        name="detalhes"
                                        value={filters.detalhes}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted">Data Inicial</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        name="start_date"
                                        value={filters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold text-muted">Data Final</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        name="end_date"
                                        value={filters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={1}>
                                <Button type="submit" variant="primary" className="w-100 fw-bold">
                                    <FiSearch />
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 text-muted small fw-bold text-uppercase ps-4">Data/Hora</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Usuário</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Ação</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Detalhes</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase text-end pe-4">IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5 text-muted">
                                        Nenhum registro encontrado.
                                    </td>
                                </tr>
                            ) : (
                                logs.map(log => (
                                    <tr key={log.id} className="align-middle">
                                        <td className="ps-4 text-nowrap" style={{ fontSize: '0.9rem' }}>
                                            {new Date(log.timestamp).toLocaleString()}
                                        </td>
                                        <td className="fw-bold text-dark">{log.usuario}</td>
                                        <td>
                                            <Badge bg="info" className="fw-normal text-dark bg-opacity-25 border border-info">
                                                {log.acao}
                                            </Badge>
                                        </td>
                                        <td className="text-muted small" style={{ maxWidth: '300px' }}>
                                            {log.detalhes}
                                        </td>
                                        <td className="text-end pe-4 text-muted small font-monospace">
                                            {log.ip_address}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
                <Card.Footer className="bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">Itens por página:</span>
                            <Form.Select 
                                size="sm" 
                                value={pagination.per_page} 
                                onChange={handlePerPageChange}
                                style={{ width: '80px' }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="75">75</option>
                                <option value="100">100</option>
                            </Form.Select>
                            <span className="text-muted small ms-2">
                                Total: {pagination.total} registros
                            </span>
                        </div>
                        
                        <div className="d-flex align-items-center gap-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={pagination.page === 1 || loading}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <span className="text-muted small mx-2">
                                Página {pagination.page} de {pagination.pages || 1}
                            </span>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={pagination.page >= pagination.pages || loading}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                Próxima
                            </Button>
                        </div>
                    </div>
                </Card.Footer>
            </Card>
        </Container>
    );
};

export default AuditLogs;
